/**
 * PROTOTYPE C — « FONDUE DANS LE PARCOURS »
 * ═════════════════════════════════════════════════════════════════════════
 * Fichier SÉPARÉ, branché sur RIEN. Il ne remplace la barre actuelle que le
 * jour où on échange une ligne dans layout/theme.liquid (voir la section
 * sections/onix-barre-c.liquid). Tant que cette ligne n'est pas échangée,
 * `sections/onix-discount-bar.liquid` continue de tourner seule, à
 * l'identique. Aucun fichier existant n'est modifié par ce prototype.
 *
 * ⛔ LA BARRE RESTE. Décision du propriétaire, non rouvrable : « on peut la
 *   convertir en quelque chose de puissant. Si y a pas la barre t'as pas de
 *   progression ou de truc qui te pousse. Supprimer c'est pas une solution. »
 *   Ce prototype ne la supprime pas : il lui retire le droit de FLOTTER en
 *   permanence. Elle est toujours là, sur toutes les pages, à chaque panier ;
 *   c'est sa PLACE qui change selon ce qu'elle a à dire.
 *
 * LE PRINCIPE, en une phrase : une ligne, un gain, une action.
 *
 * ── LES TROIS MODES ──────────────────────────────────────────────────────
 *   `flux`     — la barre est POSÉE DANS LA PAGE, elle défile avec elle, elle
 *                ne recouvre rien et n'a rien à fermer. C'est son mode par
 *                défaut : panier vide (au-dessus de la grille de collection)
 *                et page panier (juste au-dessus du bouton de paiement).
 *   `flottant` — la barre se décolle et vient en bas de l'écran. UNIQUEMENT
 *                quand elle a une demande précise à faire : le panier n'est
 *                pas vide et la marche suivante est à +1 ou +2 maillots.
 *   `aucun`    — elle se retire. Le reste du temps, l'écran est rendu au
 *                produit.
 *
 * ── POURQUOI ÇA LA REND PLUS FORTE, ET NON PLUS FAIBLE ───────────────────
 *   Une barre permanente est un décor : l'œil l'apprend en trois secondes et
 *   cesse de la lire. Une barre qui n'apparaît qu'au moment où elle demande
 *   quelque chose de faisable garde le pouvoir d'INTERROMPRE. Et elle
 *   n'apparaît jamais pour rien : à panier vide elle ne demande rien, elle
 *   PROMET, dans le fil, là où le client choisit déjà.
 *   Le levier chiffré est connu : 1 maillot = 8,53 € de marge, 4 et plus =
 *   42,58 € (juillet). La moyenne est de 2,04 maillots par commande. Le seul
 *   pas qui compte statistiquement est donc 1 → 2, et c'est le seul que cette
 *   barre demande.
 *
 * ── CE QU'ELLE NE FAIT PLUS, ET QUI NE DISPARAÎT PAS DU SITE ─────────────
 *   · l'échelle des 5 paliers vit DÉJÀ dans le pied du tiroir de panier
 *     (`[data-paliers]`, cart-drawer-promo-suggest.js, 18/08) ;
 *   · l'application du code est DÉJÀ automatique (cart-drawer-promo-suggest.js) ;
 *   · l'offre « 2e maillot » vit DÉJÀ dans le tiroir (cart-drawer-second-jersey.js).
 *   La barre ne recopie aucun des trois. Elle y CONDUIT, en un geste.
 *
 * ── ZÉRO ÉTIQUETTE MARCHAND À L'ÉCRAN ────────────────────────────────────
 *   Ce prototype n'imprime JAMAIS `tier.label`. Il imprime soit un montant
 *   CALCULÉ sur le panier réel, soit un pourcentage lu dans `tier.pct`. Les
 *   deux sont vrais dans toutes les devises et dans tous les paniers. C'est
 *   la réponse structurelle à « -70€ » (le vrai gain vaut 69,72 € sur des
 *   maillots) et à « −25% / -25€ sur le même écran hors euro ».
 *
 * ⚠️ LE CALCUL EST UNE COPIE DE cart-drawer-promo-suggest.js, PAS UNE
 *   RÉÉCRITURE. Même `Math.floor` (Shopify TRONQUE les remises en % au centime
 *   inférieur), même ordre `pct` d'abord puis étiquette, même conversion
 *   `SHOP_RATE` pour le repli en euros fixes. Si C est retenu, ces fonctions
 *   doivent DESCENDRE dans un module partagé et promo-suggest doit l'importer :
 *   deux formules pour la même chose finissent par diverger d'un centime, et
 *   c'est déjà écrit noir sur blanc dans promo-suggest ligne 183.
 *   En attendant, le banc figé ci-dessous fige les valeurs des deux côtés.
 *
 *   node theme-brouillon/assets/onix-barre-c.js --test    # 18 cas figés
 */
(function (racine) {
  'use strict';

  /* ═══════════════════════════════════════════════════════════════════════
     PARTIE PURE — aucune dépendance au DOM, donc testable en Node.
     ═══════════════════════════════════════════════════════════════════════ */

  /* ⛔ LA MARCHE MAXIMALE QUE LA BARRE A LE DROIT DE DEMANDER.
     Le palier à 9 maillots existe et reste dans l'échelle du tiroir — il paie
     quand un client à 5 monte à 9. Mais le DEMANDER à un panier de 5 quand la
     moyenne de la boutique est de 2,04 maillots par commande (juillet, 407
     cmd) et 2,41 début août, c'est afficher un mur et appeler ça une marche.
     Au-delà de +2, la barre se tait : elle n'a rien de faisable à proposer.
     Une seule constante, nommée, modifiable dans le schéma de la section. */
  var ECART_MAX_DEFAUT = 2;

  /* « -20% » → {pct:0.2} · « -30€ » → {eurCents:3000}.
     ⚠️ `tier.pct` D'ABORD — 03/09. L'étiquette « -25€ » est un arrondi
     d'accroche, le vrai taux est 25 %. Lire l'étiquette en premier faisait
     annoncer une remise fixe là où Shopify applique un pourcentage. */
  function lirePalier(tier) {
    if (tier && tier.pct != null && tier.pct !== '') {
      var n = Number(tier.pct);
      if (isFinite(n) && n > 0) return { pct: n > 1 ? n / 100 : n };
    }
    var s = String((tier && tier.label) || '').replace(/[−–-]/g, '').trim();
    if (s.indexOf('%') !== -1) return { pct: parseFloat(s) / 100 };
    var num = parseFloat(s.replace(/[^0-9.,]/g, '').replace(',', '.'));
    return { eurCents: Math.round((num || 0) * 100) };
  }

  /* La remise d'un palier, en centimes de la devise affichée.
     ⚠️ `Math.floor` et PAS `Math.round` : 15 % de 49,98 € valent 7,49 € à la
     caisse Shopify, pas 7,50 €. Un centime d'écart annoncé, c'est une promesse
     non tenue au moment du paiement. */
  function gainCentimes(tier, sousTotalCents, taux) {
    var p = lirePalier(tier);
    return p.pct != null
      ? Math.floor(p.pct * sousTotalCents)
      : Math.round((p.eurCents || 0) * (taux || 1));
  }

  function palierAtteint(tiers, count) {
    var atteint = null;
    for (var i = 0; i < tiers.length; i++) if (count >= tiers[i].qty) atteint = tiers[i];
    return atteint;
  }

  function marcheSuivante(tiers, count) {
    for (var i = 0; i < tiers.length; i++) if (tiers[i].qty > count) return tiers[i];
    return null;
  }

  /* CE QUE COÛTENT RÉELLEMENT LE(S) MAILLOT(S) SUPPLÉMENTAIRE(S).
     Prix marginal = (ce qu'il paierait au palier suivant) − (ce qu'il paie
     aujourd'hui). C'est un montant, pas un pourcentage : « 17,50 € » se lit
     sans calculer, « -15 % » demande une soustraction. */
  function prixMarginalCentimes(tiers, count, baseCents, taux, uniteCents) {
    var suiv = marcheSuivante(tiers, count);
    if (!suiv || count <= 0) return null;
    /* ⚠️ PANIER MIXTE — LE PIEGE PAYE LE 05/09. La moyenne `base / count`
       est juste tant que le panier est homogene, et FAUSSE des qu'il ne l'est
       plus : 1 veste 59,99 € + 1 maillot 24,99 € donnent 42,49 € de moyenne, donc
       une barre qui annonce le maillot suivant à un prix que rien ne vend.
       ➤ L'article ajouté se price au MOINS CHER deja dans le panier : c'est le
       seul montant dont on sait qu'il existe au catalogue. À defaut (banc,
       panier homogene) on retombe sur la moyenne, identique par construction. */
    var unite = (uniteCents != null && uniteCents > 0) ? uniteCents : (baseCents || 0) / count;
    /* Et on projette le panier REEL + les articles ajoutes, jamais `unite * qty`
       qui reecrirait le prix des articles deja choisis. */
    var projete = (baseCents || 0) + unite * (suiv.qty - count);
    var totalSuivant = projete - gainCentimes(suiv, projete, taux);
    var atteint = palierAtteint(tiers, count);
    var totalActuel = (baseCents || 0) - (atteint ? gainCentimes(atteint, baseCents || 0, taux) : 0);
    return Math.max(0, Math.round(totalSuivant - totalActuel));
  }

  /* ⛔ CE QUI OUVRE UN CLAVIER, ET CE QUI N'EN OUVRE PAS — 05/09.
     La fonction s'appelait `estChampTexte()` et acceptait `select`. Un
     `<select>` EST un champ, mais il n'ouvre AUCUN clavier : il ouvre une roue
     de choix, qui ne réduit pas le viewport. Le tri de collection
     (`sections/collection.liquid`, `#onix-collection__sort-select`) faisait
     donc taire la barre ET la carte à chaque tri — mesuré au navigateur le
     05/09 : sur un simple `focusin`, `data-raison` passait à « clavier mobile
     ouvert » et la hauteur tombait à 0.
     ➜ Le nom dit maintenant ce qu'on teste : est-ce que ÇA ouvre un clavier.
     ⚠️ ON LIT `tagName` ET `type`, PAS UN SÉLECTEUR `matches()`. C'est ce qui
       rend la fonction PURE, donc atteignable par le banc Node avec un objet
       nu — l'ancienne version vivait dans `demarrer()`, hors de portée de tout
       cas figé, et c'est exactement pour ça que le défaut a tenu.
     ⚠️ `reset`, `color`, `file`, `image` et `hidden` passent de « ouvre un
       clavier » à « n'en ouvre pas ». L'ancien sélecteur ne les excluait pas :
       aucun n'appelle de clavier, c'est une correction, pas une régression. */
  var TYPES_SANS_CLAVIER = {
    checkbox: 1, radio: 1, button: 1, submit: 1, reset: 1,
    range: 1, file: 1, image: 1, color: 1, hidden: 1
  };

  function ouvreUnClavier(el) {
    if (!el) return false;
    var t = String(el.tagName || '').toLowerCase();
    if (t === 'textarea') return true;
    if (t === 'input') return !TYPES_SANS_CLAVIER[String(el.type || 'text').toLowerCase()];
    return el.isContentEditable === true;
  }

  /* ═══ LA DÉCISION — le cœur du prototype ═══════════════════════════════
     Une fonction PURE qui répond à une seule question : où va la barre, et
     pourquoi. Elle rend toujours une raison en clair : un mode sans raison
     lisible est un mode qu'on ne peut pas discuter.
     Ordre des tests : du plus impératif au plus contextuel. */
  function decider(etat) {
    var ecartMax = etat.ecartMax == null ? ECART_MAX_DEFAUT : etat.ecartMax;

    if (etat.page === 'product')
      return { mode: 'aucun', raison: 'fiche produit — le bouton d’ajout collant dit déjà quoi faire (décision 23/08)' };
    if (etat.tiroirOuvert)
      return { mode: 'aucun', raison: 'tiroir de panier ouvert — l’échelle et l’offre y sont déjà, en entier' };
    if (etat.clavier)
      return { mode: 'aucun', raison: 'clavier mobile ouvert — le viewport réduit collerait la barre au milieu de l’écran' };
    if (etat.cookies)
      return { mode: 'aucun', raison: 'bandeau cookies levé — il occupe déjà le bas' };

    if (etat.page === 'cart')
      return { mode: 'flux', raison: 'page panier — dans le flux, au-dessus du bouton de paiement' };

    if (!etat.count || etat.count <= 0) {
      return etat.ancre
        ? { mode: 'flux', raison: 'panier vide — la promesse est posée dans le fil, au-dessus de la grille' }
        : { mode: 'aucun', raison: 'panier vide et aucune ancre dans cette page — rien à promettre ici' };
    }

    if (etat.ferme)
      return { mode: 'aucun', raison: 'fermée par le client, et le panier n’a pas bougé depuis' };

    var suiv = marcheSuivante(etat.tiers || [], etat.count);
    if (!suiv)
      return { mode: 'aucun', raison: 'dernier palier atteint — plus rien à demander, le tiroir affiche « réduction max »' };

    var ecart = suiv.qty - etat.count;
    if (ecart > ecartMax)
      return {
        mode: 'aucun',
        raison: 'marche suivante à +' + ecart + ' — au-delà de +' + ecartMax + ' c’est un mur, pas une marche',
        ecart: ecart
      };

    return { mode: 'flottant', raison: 'marche à +' + ecart + ', atteignable', ecart: ecart };
  }

  var noyau = {
    ECART_MAX_DEFAUT: ECART_MAX_DEFAUT,
    lirePalier: lirePalier,
    gainCentimes: gainCentimes,
    palierAtteint: palierAtteint,
    marcheSuivante: marcheSuivante,
    prixMarginalCentimes: prixMarginalCentimes,
    ouvreUnClavier: ouvreUnClavier,
    decider: decider
  };

  /* ═══════════════════════════════════════════════════════════════════════
     BANC FIGÉ — `node theme-brouillon/assets/onix-barre-c.js --test`
     Chacun de ces cas rejoue une objection nommée. Un banc validé seulement
     sur ce qu'il refuse ne prouve rien : la moitié des cas sont des « doit
     PASSER » (la barre DOIT se montrer).
     ═══════════════════════════════════════════════════════════════════════ */
  var PALIERS_REELS = [
    { qty: 2, label: '-15%', pct: 15 },
    { qty: 3, label: '-20%', pct: 20 },
    { qty: 4, label: '-25€', pct: 25 },
    { qty: 5, label: '-35€', pct: 28 },
    { qty: 9, label: '-70€', pct: 31 }
  ];

  function banc() {
    var T = PALIERS_REELS, ok = 0, ko = 0;
    function verifier(nom, obtenu, attendu) {
      var bon = String(obtenu) === String(attendu);
      if (bon) { ok++; console.log('  ✅ ' + nom + '  → ' + obtenu); }
      else { ko++; console.log('  ⛔ ' + nom + '  → ' + obtenu + '   (attendu ' + attendu + ')'); }
    }

    console.log('\n── PRIX MARGINAL, en centimes (objection 7 : un montant CALCULÉ, jamais une étiquette)');
    /* 1 maillot à 24,99 € : 2×24,99 = 49,98 ; −15 % = 7,49 (floor) ; 42,49 − 24,99 */
    verifier('1 maillot 24,99 € → +1', prixMarginalCentimes(T, 1, 2499, 1), 1750);
    /* 2 maillots : 3×24,99 = 74,97 ; −20 % = 14,99 ; 59,98 − 42,49 */
    verifier('2 maillots       → +1', prixMarginalCentimes(T, 2, 4998, 1), 1749);
    /* 3 maillots : 4×24,99 = 99,96 ; −25 % = 24,99 ; 74,97 − (74,97 − 14,99)
       ⚠️ J'AVAIS ÉCRIT 1749 ICI, PAR RECOPIE DE LA LIGNE DU DESSUS, et c'est
         le banc qui l'a attrapé au premier lancement. Un banc dont tous les
         cas passent du premier coup n'a rien prouvé. */
    verifier('3 maillots       → +1', prixMarginalCentimes(T, 3, 7497, 1), 1499);
    verifier('4 maillots       → +1', prixMarginalCentimes(T, 4, 9996, 1), 1500);
    /* 1 veste à 59,99 € : 2×59,99 = 119,98 ; −15 % = 17,99 ; 101,99 − 59,99 */
    verifier('1 veste 59,99 €  → +1', prixMarginalCentimes(T, 1, 5999, 1), 4200);
    /* ⛔ LE PANIER MIXTE — le defaut du 05/09, et les deux cas qui le tiennent.
       1 veste 59,99 € + 1 maillot 24,99 € = 84,98 €, moins cher a l'unite 24,99 €.
       Palier 3 : 84,98 + 24,99 = 109,97 ; −20 % = 21,99 → 87,98.
       Palier 2 aujourd'hui : 84,98 − 15 % (12,74) = 72,24.  87,98 − 72,24 = 15,74 €.
       AVANT LE CORRECTIF la moyenne (42,49 €) sortait 29,74 € — un prix que rien
       ne vend, presque le double. Le cas du dessous FIGE l'ancienne valeur pour
       qu'une regression la fasse rougir. */
    verifier('mixte veste+maillot → +1 au moins cher', prixMarginalCentimes(T, 2, 8498, 1, 2499), 1574);
    verifier('mixte SANS le moins cher → l’ancien faux', prixMarginalCentimes(T, 2, 8498, 1), 2974);
    verifier('panier vide      → rien', prixMarginalCentimes(T, 0, 0, 1), null);
    verifier('9 maillots       → rien (dernier palier)', prixMarginalCentimes(T, 9, 22491, 1), null);

    console.log('\n── LE GAIN RÉEL D’UN PALIER (objection 7 : « -70€ » et « -25€ » sont faux)');
    /* 9 × 24,99 = 224,91 ; 31 % = 69,7221 → 69,72 €. L'étiquette dit « -70€ ». */
    verifier('palier 9, 9 maillots   étiquette « -70€ »', gainCentimes(T[4], 22491, 1), 6972);
    /* 4 × 59,99 = 239,96 ; 25 % = 59,99 €. L'étiquette dit « -25€ ». */
    verifier('palier 4, 4 vestes     étiquette « -25€ »', gainCentimes(T[2], 23996, 1), 5999);
    /* 4 × 24,99 = 99,96 ; 25 % = 24,99 €. */
    verifier('palier 4, 4 maillots   étiquette « -25€ »', gainCentimes(T[2], 9996, 1), 2499);

    console.log('\n── LA DÉCISION — DOIT SE MONTRER (moitié du banc, sinon il ne prouve rien)');
    verifier('1 maillot, marche à +1', decider({ count: 1, tiers: T }).mode, 'flottant');
    verifier('4 maillots, marche à +1', decider({ count: 4, tiers: T }).mode, 'flottant');
    verifier('panier vide + ancre', decider({ count: 0, tiers: T, ancre: true }).mode, 'flux');
    verifier('page panier, 3 articles', decider({ count: 3, tiers: T, page: 'cart' }).mode, 'flux');
    verifier('page panier, panier vide', decider({ count: 0, tiers: T, page: 'cart' }).mode, 'flux');

    console.log('\n── LA DÉCISION — DOIT SE TAIRE');
    /* objection 9 : 5 maillots, prochaine marche à 9 = +4. Un mur. */
    verifier('5 maillots, marche à +4 (le mur à 9)', decider({ count: 5, tiers: T }).mode, 'aucun');
    verifier('9 maillots, dernier palier', decider({ count: 9, tiers: T }).mode, 'aucun');
    verifier('fiche produit', decider({ count: 1, tiers: T, page: 'product' }).mode, 'aucun');
    verifier('tiroir ouvert', decider({ count: 1, tiers: T, tiroirOuvert: true }).mode, 'aucun');
    verifier('clavier ouvert', decider({ count: 1, tiers: T, clavier: true }).mode, 'aucun');
    verifier('cookies à répondre', decider({ count: 1, tiers: T, cookies: true }).mode, 'aucun');
    verifier('fermée par le client', decider({ count: 1, tiers: T, ferme: true }).mode, 'aucun');
    verifier('panier vide sans ancre', decider({ count: 0, tiers: T }).mode, 'aucun');

    console.log('\n── LE CLAVIER — ce qui en ouvre un, et ce qui n’en ouvre pas');
    /* ⛔ LE DÉFAUT DU 05/09 : `select` était accepté, et le tri de collection
       est un `<select>`. La barre se taisait à chaque tri, sans clavier. */
    verifier('<select> de tri → aucun clavier, doit PASSER', ouvreUnClavier({ tagName: 'SELECT' }), false);
    verifier('… et la barre RESTE : la décision suit, doit PASSER',
      decider({ count: 1, tiers: T, clavier: ouvreUnClavier({ tagName: 'SELECT' }) }).mode, 'flottant');
    verifier('<input type=text> → clavier : la barre est REFUSÉE',
      ouvreUnClavier({ tagName: 'INPUT', type: 'text' }), true);
    verifier('… et la décision suit : elle se tait, REFUSÉE',
      decider({ count: 1, tiers: T, clavier: ouvreUnClavier({ tagName: 'INPUT', type: 'text' }) }).mode, 'aucun');
    verifier('<input> sans type (texte par défaut) → REFUSÉE', ouvreUnClavier({ tagName: 'INPUT' }), true);
    verifier('<input type=email> (la newsletter) → REFUSÉE', ouvreUnClavier({ tagName: 'INPUT', type: 'email' }), true);
    verifier('<textarea> → REFUSÉE', ouvreUnClavier({ tagName: 'TEXTAREA' }), true);
    verifier('contenteditable → REFUSÉE', ouvreUnClavier({ tagName: 'DIV', isContentEditable: true }), true);
    verifier('<input type=checkbox> → doit PASSER', ouvreUnClavier({ tagName: 'INPUT', type: 'checkbox' }), false);
    verifier('<input type=range> → doit PASSER', ouvreUnClavier({ tagName: 'INPUT', type: 'range' }), false);
    verifier('<body> — rien de focalisé → doit PASSER', ouvreUnClavier({ tagName: 'BODY' }), false);
    verifier('<a> — un lien → doit PASSER', ouvreUnClavier({ tagName: 'A' }), false);
    verifier('null → doit PASSER', ouvreUnClavier(null), false);

    console.log('\n── LA FRONTIÈRE « ecart == ecartMax » — le seuil qu’aucun cas ne tenait');
    /* ⛔ TROUVÉ PAR MUTATION le 05/09, pas par relecture : passer
       `ecart > ecartMax` à `ecart >= ecartMax` ne faisait rougir AUCUN des deux
       bancs (25 + 114 = 139 cas verts) et faisait disparaître la barre pour tout
       panier à exactement +2 du palier suivant — le cas le plus courant.
       Les deux cas ci-dessous tiennent la frontière des DEUX côtés. */
    verifier('ecart == ecartMax : 7 maillots, marche à +2 → doit PASSER',
      decider({ count: 7, tiers: T }).mode, 'flottant');
    verifier('… et l’écart annoncé est bien 2, doit PASSER', decider({ count: 7, tiers: T }).ecart, 2);
    verifier('ecart == ecartMax + 1 : 6 maillots, marche à +3 → REFUSÉE',
      decider({ count: 6, tiers: T }).mode, 'aucun');
    /* La même frontière sur une échelle minimale, `ecartMax` passé en clair :
       c'est le cas de la sonde du 05/09 — palier à 3, panier de 1. */
    verifier('+2 exactement sur un palier à 3, ecartMax=2 → doit PASSER',
      decider({ count: 1, tiers: [{ qty: 3, pct: 20 }], ecartMax: 2 }).mode, 'flottant');
    verifier('+3 sur un palier à 4, ecartMax=2 → REFUSÉE',
      decider({ count: 1, tiers: [{ qty: 4, pct: 25 }], ecartMax: 2 }).mode, 'aucun');
    /* Et avec `ecartMax = 1`, la frontière se déplace : elle est bien LUE,
       jamais figée à 2. */
    verifier('+1 avec ecartMax=1 → doit PASSER',
      decider({ count: 1, tiers: [{ qty: 2, pct: 15 }], ecartMax: 1 }).mode, 'flottant');
    verifier('+2 avec ecartMax=1 → REFUSÉE',
      decider({ count: 1, tiers: [{ qty: 3, pct: 20 }], ecartMax: 1 }).mode, 'aucun');

    console.log('\n' + (ko ? '⛔ ' + ko + ' cas en échec sur ' + (ok + ko) : '✅ ' + ok + ' cas figés passent'));
    return ko;
  }

  /* ── Sortie NODE : le banc, puis on s'arrête. Aucun code navigateur ne
        s'exécute côté Node, et réciproquement. ─────────────────────────── */
  if (typeof window === 'undefined') {
    if (typeof process !== 'undefined' && process.argv && process.argv.indexOf('--test') !== -1) {
      process.exit(banc() ? 1 : 0);
    }
    return;
  }

  racine.OnixBarreC = noyau;
  noyau.banc = banc;

  /* ═══════════════════════════════════════════════════════════════════════
     PARTIE NAVIGATEUR
     ═══════════════════════════════════════════════════════════════════════ */
  function demarrer() {
    var barre = document.getElementById('onix-barre-c');
    if (!barre) return;

    /* ⚠️ LES DEUX BARRES DANS LA MÊME PAGE SE DISPUTENT `--onix-bar-height`,
       que back-to-top.liquid et le bouton collant de la fiche produit lisent
       pour se poser au-dessus. Deux écrivains, une variable : la flèche se
       poserait au petit bonheur. On le DIT, on ne le devine pas. */
    if (document.getElementById('onix-discount-bar')) {
      try {
        console.warn('[onix-barre-c] La barre actuelle (#onix-discount-bar) est encore dans la page. '
          + 'Les deux écrivent --onix-bar-height. Pour essayer le prototype C, remplacer dans '
          + 'layout/theme.liquid la ligne {% section \'onix-discount-bar\' %} par {% section \'onix-barre-c\' %}.');
      } catch (e) {}
    }

    var tiers;
    try { tiers = JSON.parse(barre.dataset.tiers || '[]'); } catch (e) { return; }
    if (!tiers.length) return;
    tiers.sort(function (a, b) { return a.qty - b.qty; });

    var ecartMax = parseInt(barre.dataset.ecartMax, 10);
    if (!isFinite(ecartMax) || ecartMax < 1) ecartMax = ECART_MAX_DEFAUT;

    var page = barre.dataset.page || '';
    var texteEl = document.getElementById('onix-barre-c-texte');
    var actionEl = document.getElementById('onix-barre-c-action');
    var fermerEl = document.getElementById('onix-barre-c-fermer');
    var jaugeEl = document.getElementById('onix-barre-c-jauge');
    var place = document.getElementById('onix-barre-c-place');

    var T = {
      restant: barre.dataset.tRestant || 'Add {{n}} {{item}} for {{price}}',
      item: barre.dataset.tItem || 'jersey',
      items: barre.dataset.tItems || 'jerseys',
      egale: barre.dataset.tEgale || '{{qty}} {{jerseys}} = {{reward}}',
      ajouter: barre.dataset.tAjouter || 'Add',
      unite: barre.dataset.tUnite || 'jerseys',
      economie: barre.dataset.tEconomie || 'You save {{amount}}'
    };

    /* Devise — miroir exact de fmtMoney() de cart-drawer-promo-suggest.js. */
    var tiroirRacine = document.querySelector('[data-cart-drawer-root]');
    var DEVISE = (tiroirRacine && tiroirRacine.dataset.cartCurrency)
      || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'EUR';
    var TAUX = parseFloat((window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1) || 1;
    function montant(cents) {
      var a = (cents || 0) / 100;
      if (DEVISE === 'EUR') return a.toFixed(2).replace('.', ',') + '€';
      try { return new Intl.NumberFormat('en', { style: 'currency', currency: DEVISE, currencyDisplay: 'narrowSymbol' }).format(a); }
      catch (e) {
        try { return new Intl.NumberFormat('en', { style: 'currency', currency: DEVISE }).format(a); }
        catch (e2) { return a.toFixed(2) + ' ' + DEVISE; }
      }
    }
    /* Un POURCENTAGE est vrai dans toutes les devises — c'est pour ça qu'il
       remplace l'étiquette quand aucun panier n'existe encore. */
    function pourcent(tier) {
      var p = lirePalier(tier);
      if (p.pct == null) return null;
      return '−' + Math.round(p.pct * 100) + '%';
    }
    function echapper(s) {
      return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
        return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
      });
    }

    /* ── ÉTAT LU, JAMAIS DEVINÉ ──────────────────────────────────────── */
    var LROOT = (window.OnixCart && window.OnixCart.cartRoot)
      || (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    if (LROOT.slice(-1) !== '/') LROOT += '/';

    var count = 0, base = 0, uniteMin = null;
    var clavierOuvert = false;
    var CLE_FERME = 'onix_barre_c_ferme_v1';

    /* Fermée pour CE panier-là seulement. Un panier qui change, c'est une
       information neuve : la barre a le droit de revenir. « Fermée » n'est
       pas « éteinte ». */
    function estFermee(c) {
      try {
        var v = window.sessionStorage.getItem(CLE_FERME);
        return v !== null && v === String(c);
      } catch (e) { return false; }
    }
    function marquerFermee(c) {
      try { window.sessionStorage.setItem(CLE_FERME, String(c)); } catch (e) {}
    }

    function tiroirOuvert() {
      return !!(tiroirRacine && tiroirRacine.classList.contains('is-open'));
    }
    function cookiesLeves() {
      return document.documentElement.classList.contains('onix-cookies-a-repondre');
    }

    /* ── LES ANCRES DU MODE « FLUX » ─────────────────────────────────────
       Premier trouvé gagne. Aucune n'est obligatoire : sans ancre, le mode
       flux est simplement indisponible sur cette page et la barre se tait —
       elle ne se rabat JAMAIS en flottante par défaut, ce serait revenir au
       défaut qu'on corrige. */
    function trouverAncre() {
      if (page === 'cart') {
        var resume = document.querySelector('.onix-cart-page__summary');
        var bouton = document.querySelector('.onix-cart-page__checkout');
        if (resume && bouton) return { parent: resume, avant: bouton };
        return null;
      }
      var explicite = document.querySelector('[data-onix-barre-ancre]');
      if (explicite) return { parent: explicite, avant: null };
      /* La barre d'outils de collection : juste au-dessus de la grille,
         exactement là où le client s'apprête à choisir. */
      var barreOutils = document.querySelector('.onix-collection__toolbar');
      if (barreOutils && barreOutils.parentNode)
        return { parent: barreOutils.parentNode, avant: barreOutils.nextElementSibling };
      return null;
    }

    /* ── PLACER : le seul endroit qui touche au DOM ─────────────────────
       ⚠️ On garde un PLACEHOLDER de la hauteur mesurée en flux. Sans lui, le
       passage flux → flottant (0 → 1 article sur une collection) fait
       remonter la grille de la hauteur de la barre, sous les doigts du
       client, au moment précis où il vient d'ajouter. */
    var ancrePosee = null;
    function placer(mode) {
      if (mode === 'flux') {
        var a = trouverAncre();
        if (!a) return false;
        if (barre.parentNode !== a.parent || (a.avant && barre.nextElementSibling !== a.avant)) {
          if (a.avant) a.parent.insertBefore(barre, a.avant);
          else a.parent.appendChild(barre);
        }
        ancrePosee = a;
        if (place) place.style.height = '0px';
        return true;
      }
      /* flottant : la barre repart en fin de <body>, et le placeholder prend
         sa hauteur dans le flux si elle en occupait une. */
      if (ancrePosee && place) {
        var h = Math.round(barre.getBoundingClientRect().height);
        if (h > 2 && place.parentNode !== ancrePosee.parent) {
          ancrePosee.parent.insertBefore(place, barre);
        }
        if (h > 2) place.style.height = h + 'px';
      }
      if (barre.parentNode !== document.body) document.body.appendChild(barre);
      return true;
    }

    /* ── PUBLIER LA HAUTEUR ────────────────────────────────────────────
       back-to-top.liquid et le bouton collant de la fiche produit lisent
       `--onix-bar-height` pour se poser AU-DESSUS. Une barre en mode `flux`
       ne recouvre rien : elle publie 0. C'est le point de l'objection 5,
       et c'est mécanique, pas déclaratif. */
    function publierHauteur(mode) {
      var h = 0;
      if (mode === 'flottant' && !barre.hidden) {
        var r = barre.getBoundingClientRect();
        h = Math.ceil(r.height) + 10;   /* 10px = le décalage bas de la barre */
      }
      document.documentElement.style.setProperty('--onix-bar-height', h + 'px');
    }

    /* ── LE RENDU ──────────────────────────────────────────────────────── */
    var dernierMode = null;

    function rendre() {
      var d = decider({
        count: count,
        tiers: tiers,
        ecartMax: ecartMax,
        page: page,
        tiroirOuvert: tiroirOuvert(),
        clavier: clavierOuvert,
        cookies: cookiesLeves(),
        ferme: estFermee(count),
        ancre: !!trouverAncre()
      });

      barre.dataset.raison = d.raison;   /* lisible dans l'inspecteur, et testable */

      if (d.mode === 'aucun') {
        barre.hidden = true;
        barre.classList.remove('onix-barre-c--flottante', 'onix-barre-c--flux');
        if (place) place.style.height = '0px';
        dernierMode = 'aucun';
        publierHauteur('aucun');
        return;
      }

      if (!placer(d.mode)) {
        barre.hidden = true;
        dernierMode = 'aucun';
        publierHauteur('aucun');
        return;
      }

      barre.classList.toggle('onix-barre-c--flottante', d.mode === 'flottant');
      barre.classList.toggle('onix-barre-c--flux', d.mode === 'flux');

      var suiv = marcheSuivante(tiers, count);
      var atteint = palierAtteint(tiers, count);

      if (count <= 0) {
        /* LA PROMESSE — deux premières marches, en pourcentages CALCULÉS.
           Le mot « maillots » n'est écrit qu'une fois : le lecteur apprend
           l'unité et la reporte (règle de légende d'axe, 18/08). */
        var bouts = [];
        for (var i = 0; i < Math.min(2, tiers.length); i++) {
          var pc = pourcent(tiers[i]);
          if (!pc) continue;
          bouts.push(T.egale
            .replace('{{qty}}', echapper(String(tiers[i].qty)))
            .replace('{{jerseys}}', i === 0 ? echapper(T.unite) : '')
            .replace('{{reward}}', '<strong>' + echapper(pc) + '</strong>')
            .replace(/\s{2,}/g, ' ').trim());
        }
        texteEl.innerHTML = bouts.join(' · ');
        actionEl.hidden = true;             /* pas de gain, donc pas d'action */
        if (fermerEl) fermerEl.hidden = true;  /* rien à fermer : ça défile */
        if (jaugeEl) jaugeEl.style.width = '0%';
      } else if (suiv) {
        var manque = suiv.qty - count;
        var marginal = prixMarginalCentimes(tiers, count, base, TAUX, uniteMin);
        texteEl.innerHTML = T.restant
          .replace('{{n}}', '<strong>' + echapper(String(manque)) + '</strong>')
          .replace('{{item}}', echapper(manque > 1 ? T.items : T.item))
          .replace('{{price}}', '<strong class="onix-barre-c__prix">' + echapper(montant(marginal)) + '</strong>');
        actionEl.hidden = false;
        actionEl.textContent = T.ajouter;
        if (fermerEl) fermerEl.hidden = (d.mode !== 'flottant');
        if (jaugeEl) {
          /* Remplissage PAR SEGMENTS, jamais `count / suiv.qty` : avec le
             rapport brut la jauge RECULE au changement de segment (4/5 = 80 %
             puis 5/9 = 56 %). Ici chaque palier vaut une tranche égale, donc
             elle ne descend jamais. Même logique que renderGauge(). */
          var iSeg = tiers.indexOf(suiv);
          var debut = atteint ? atteint.qty : 0;
          var portee = Math.max(1, suiv.qty - debut);
          var dedans = Math.min(1, Math.max(0, (count - debut) / portee));
          jaugeEl.style.width = Math.min(100, Math.round(((iSeg + dedans) / tiers.length) * 100)) + '%';
        }
      } else {
        /* mode `flux` sur la page panier, dernier palier atteint : on ne
           demande rien, on constate.
           ⚠️ PREMIER JET : un montant NU, « 69,72€ », sans un mot autour. Un
             chiffre sans phrase ne se lit pas — le client ne sait pas si c'est
             ce qu'il paie ou ce qu'il gagne. `cart.progress_savings` existe et
             est traduite dans les six langues : « Tu économises déjà {{amount}} ». */
        var gain = atteint ? gainCentimes(atteint, base, TAUX) : 0;
        texteEl.innerHTML = T.economie.replace('{{amount}}',
          '<strong class="onix-barre-c__prix">' + echapper(montant(gain)) + '</strong>');
        actionEl.hidden = true;
        if (fermerEl) fermerEl.hidden = true;
        if (jaugeEl) jaugeEl.style.width = '100%';
      }

      barre.hidden = false;
      dernierMode = d.mode;
      requestAnimationFrame(function () { publierHauteur(d.mode); });
    }

    /* ── L'ACTION : UN SEUL GESTE, VERS CE QUI EXISTE DÉJÀ ───────────────
       Le tiroir porte l'offre « 2e maillot » (même modèle, taille en une
       ligne), les suggestions, l'échelle des paliers et l'application
       automatique du code. La barre n'en refait aucun : elle ouvre le tiroir.
       Chemin complet : [Ajouter] → taille → [Ajouter au panier] = 3 gestes. */
    actionEl.addEventListener('click', function (e) {
      e.preventDefault();
      e.stopPropagation();
      var pont = window.OnixCart && window.OnixCart.bridge;
      if (pont && typeof pont.openDrawer === 'function') { pont.openDrawer(); return; }
      if (typeof window.__onixOpenDrawer === 'function') { window.__onixOpenDrawer(); return; }
      window.location.href = LROOT + 'cart';   /* repli sans JS de tiroir */
    });

    if (fermerEl) {
      fermerEl.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        marquerFermee(count);
        rendre();
      });
    }

    /* ⛔ LA BARRE ELLE-MÊME N'EST PAS UN BOUTON. Rien d'autre que [Ajouter]
       et [✕] ne réagit au doigt, et le curseur ne ment pas : `cursor:default`
       sur le conteneur (voir la feuille de style de la section). Un tap dans
       le vide ne referme rien et ne déclenche rien. */

    /* ── LECTURE DU PANIER — même découpe bundle que la barre actuelle ─── */
    var enVol = false, enAttente = false, minuteur = null;

    function lireEtRendre() {
      return fetch(LROOT + 'cart.js', { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (panier) {
          var bundle = 0, bundleCents = 0;
          (panier.items || []).forEach(function (i) {
            if (i.properties && i.properties._offer_bundle === 'true') {
              bundle += (i.quantity || 0);
              bundleCents += (i.final_line_price || i.line_price || 0);
            }
          });
          /* Indice optimiste posé par cart-drawer.js pendant que Shopify n'a
             pas encore rendu les propriétés `_offer_bundle` : sans lui la
             barre se remplit puis retombe à 0 quelques centaines de ms plus
             tard, sous les yeux du client. */
          var opt = Number(window.__onixOptimisticBundleQty || 0);
          var jusqua = Number(window.__onixOptimisticBundleUntil || 0);
          var effectif = (opt > 0 && Date.now() < jusqua) ? Math.max(bundle, opt) : bundle;

          count = Math.max(0, (panier.item_count || 0) - effectif);
          /* ⚠️ LA MÊME SOMME QUE updateProgress() DE promo-suggest : on ADDITIONNE
             les lignes non-bundle. Premier jet : `items_subtotal_price − total
             bundle`, qui donne le même chiffre aujourd'hui mais partirait de
             deux définitions différentes — celle de Shopify et la nôtre. Le jour
             où une remise de ligne s'applique à un côté et pas à l'autre, la
             barre et le tiroir annoncent deux montants pour le même panier. */
          base = (panier.items || []).reduce(function (s, i) {
            var estBundle = i.properties && i.properties._offer_bundle === 'true';
            return s + (estBundle ? 0 : (i.final_line_price || i.line_price || 0));
          }, 0);
          /* LE MOINS CHER A L UNITE, hors bundle — c'est lui qui price l'article
             suivant. Panier homogene : identique a la moyenne. Panier mixte :
             c'est la seule valeur qui existe vraiment au catalogue. */
          uniteMin = null;
          (panier.items || []).forEach(function (i) {
            if (i.properties && i.properties._offer_bundle === 'true') return;
            var u = i.final_price || i.price || 0;
            if (u > 0 && (uniteMin === null || u < uniteMin)) uniteMin = u;
          });
          rendre();
        })
        .catch(function () { /* on garde le dernier état connu */ });
    }

    function rafraichir() {
      if (enVol) { enAttente = true; return; }
      if (minuteur) return;
      minuteur = setTimeout(function () {
        minuteur = null;
        enVol = true;
        lireEtRendre().then(function () {
          enVol = false;
          if (enAttente) { enAttente = false; rafraichir(); }
        });
      }, 50);
    }

    document.addEventListener('cart:refresh', rafraichir);
    window.addEventListener('pageshow', function (e) { if (e.persisted) rafraichir(); });
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') rafraichir();
    });
    window.addEventListener('resize', function () { rendre(); });

    if (tiroirRacine && typeof MutationObserver !== 'undefined') {
      new MutationObserver(rendre).observe(tiroirRacine, { attributes: true, attributeFilter: ['class'] });
    }
    if (typeof MutationObserver !== 'undefined') {
      new MutationObserver(rendre).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    }
    var compteurs = document.querySelectorAll('[data-cart-count]');
    if (compteurs.length && typeof MutationObserver !== 'undefined') {
      var dernier = compteurs[0].textContent.trim();
      new MutationObserver(function () {
        var neuf = compteurs[0].textContent.trim();
        if (neuf !== dernier) { dernier = neuf; rafraichir(); }
      }).observe(compteurs[0], { childList: true, characterData: true, subtree: true });
    }

    /* Clavier mobile — un fixed en bas vient se coller au-dessus du clavier,
       donc en plein milieu de l'écran. focusout différé pour qu'un passage
       d'un champ à l'autre ne fasse pas clignoter la barre.
       Le test est `ouvreUnClavier()`, dans la partie pure : il est figé au banc
       dans les deux sens, et un `<select>` n'y déclenche plus rien. */
    document.addEventListener('focusin', function (e) {
      if (ouvreUnClavier(e.target)) { clavierOuvert = true; rendre(); }
    });
    document.addEventListener('focusout', function () {
      setTimeout(function () {
        if (!ouvreUnClavier(document.activeElement)) { clavierOuvert = false; rendre(); }
      }, 150);
    });

    /* Exposé pour l'inspection et pour le banc navigateur. */
    racine.OnixBarreC.etat = function () {
      return {
        count: count, base: base, mode: dernierMode,
        raison: barre.dataset.raison,
        hauteur: Math.round(barre.getBoundingClientRect().height)
      };
    };
    racine.OnixBarreC.simuler = function (c, b) { count = c; base = b; rendre(); };

    rafraichir();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', demarrer);
  else demarrer();
})(typeof window !== 'undefined' ? window : globalThis);
